import type { IRestrictedArea, IRestrictedAreaInput, IRestrictedAreaOptions } from '@d-orca/shared/alert-kinds';

/** A pick that can also mean "every one": the dialog's professions and certificates. */
export type AllOrIds = 'all' | string[];

type AllOrIdsOf<T> = 'all' | T[];

const EMPTY: IRestrictedAreaInput = { floorIds: [], areaIds: [], professionIds: [], certificateIds: [] };

/** The dialog's starting draft: empty to create, the area's own ids to edit. */
export function draftFrom(area: IRestrictedArea | null): IRestrictedAreaInput {
  if (!area) return EMPTY;
  const ids = (items: AllOrIdsOf<{ id: string }>): AllOrIds => (items === 'all' ? 'all' : items.map((i) => i.id));
  return {
    floorIds: area.floors.map((f) => f.id),
    areaIds: area.areas.map((a) => a.id),
    professionIds: ids(area.professions),
    certificateIds: ids(area.certificates),
  };
}

const isPicked = (pick: AllOrIds) => pick === 'all' || pick.length > 0;

/** At least one place, closed to at least one profession or opened by at least one certificate. */
export function canSubmit(draft: IRestrictedAreaInput): boolean {
  return draft.floorIds.length + draft.areaIds.length > 0 && (isPicked(draft.professionIds) || isPicked(draft.certificateIds));
}

/** Order-insensitive: picking A then B is the same area as B then A. */
function normalize(draft: IRestrictedAreaInput): string {
  const sorted = (pick: AllOrIds) => (pick === 'all' ? pick : [...pick].sort());
  return JSON.stringify([
    sorted(draft.floorIds),
    sorted(draft.areaIds),
    sorted(draft.professionIds),
    sorted(draft.certificateIds),
  ]);
}

export function isChanged(draft: IRestrictedAreaInput, initial: IRestrictedAreaInput): boolean {
  return normalize(draft) !== normalize(initial);
}

/** One row of an all-or-ids pick toggled. Unticking a row under "all" keeps every other one. */
export function togglePick(current: AllOrIds, allIds: string[], id: string, next: boolean): string[] {
  const ids = current === 'all' ? allIds : current;
  return next ? [...ids, id] : ids.filter((x) => x !== id);
}

/** One id of a plain list toggled. */
export function toggleId(ids: string[], id: string, next: boolean): string[] {
  return togglePick(ids, ids, id, next);
}

/** `extra` items `base` does not already hold, by id, after `base`. */
function adding<T extends { id: string }>(base: T[], extra: T[] | 'all'): T[] {
  if (extra === 'all') return base;
  const known = new Set(base.map((b) => b.id));
  return [...base, ...extra.filter((e) => !known.has(e.id))];
}

/**
 * The project's options, plus whatever the edited area already holds that has
 * since left them (a profession removed from the project) — so it is shown,
 * and can be unticked, rather than kept unseen in the draft.
 */
export function optionsFor(options: IRestrictedAreaOptions, area: IRestrictedArea | null): IRestrictedAreaOptions {
  if (!area) return options;
  return {
    floors: adding(options.floors, area.floors),
    areas: adding(options.areas, area.areas),
    professions: adding(options.professions, area.professions),
    certificates: adding(options.certificates, area.certificates),
  };
}
